import { ChevronLeft, ChevronRight } from 'lucide-react';
import React from 'react';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';

interface PaginationSectionProps {
  page: number;
  onChangePage: (page: number) => void;
  totalPages: number;
}

const PAGE_SIZE = 10; // 한 번에 보여줄 페이지 수

const PaginationSection = ({ page, onChangePage, totalPages }: PaginationSectionProps) => {
  const isPreviewMode = useMembershipPreviewMode();
  const currentPage = page + 1;

  // 현재 페이지가 속한 그룹(1~10, 11~20, ...)
  const currentGroup = Math.floor((currentPage - 1) / PAGE_SIZE);
  const groupStart = currentGroup * PAGE_SIZE + 1;
  const groupEnd = Math.min(groupStart + PAGE_SIZE - 1, totalPages);

  const pageNumbers = Array.from({ length: groupEnd - groupStart + 1 }, (_, i) => groupStart + i);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    onChangePage(page - 1);
  };

  return (
    <div
      className="flex w-full items-center justify-center gap-3"
      data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
    >
      {/* 왼쪽 화살표 */}
      <PaginationButton onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
        <ChevronLeft className="h-5 w-5" />
      </PaginationButton>

      {/* 이전 그룹 ... 버튼 */}
      {groupStart > 1 && (
        <PaginationButton onClick={() => goToPage(groupStart - 1)}>...</PaginationButton>
      )}

      {/* 페이지 번호 */}
      <div className="flex items-center gap-3">
        {pageNumbers.map((num) => (
          <PaginationButton key={num} onClick={() => goToPage(num)} active={num === currentPage}>
            {num}
          </PaginationButton>
        ))}
      </div>

      {/* 다음 그룹 ... 버튼 */}
      {groupEnd < totalPages && (
        <PaginationButton onClick={() => goToPage(groupEnd + 1)}>...</PaginationButton>
      )}

      {/* 오른쪽 화살표 */}
      <PaginationButton
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-5 w-5" />
      </PaginationButton>
    </div>
  );
};

interface PaginationButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const PaginationButton: React.FC<PaginationButtonProps> = ({
  active = false,
  disabled = false,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex h-5 w-5 items-center justify-center transition-colors Body4S14
      ${active ? 'font-bold text-point-500' : 'text-gr-500'}
      hover:text-point-500
      disabled:text-gr-300`}
    aria-current={active ? 'page' : undefined}
  >
    {children}
  </button>
);

export default PaginationSection;
